import { useTheme } from "@emotion/react";
import { Alert, Box, Snackbar } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ReactComponent as SearchIcon } from "../../../assets/icons/dashboard/user/searchIcon.svg";
import AddButton from "../../../components/AddButton";
import TableLayout from "../../../components/TableLayout";
import api from "../../../api/apiClient";
import { format, toZonedTime } from "date-fns-tz";
import { useAuth } from "../../../context/AuthContext";

export default function Role() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useAuth();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const canCreate = user?.permissions?.role?.create;
  const canEdit = user?.permissions?.role?.edit;
  const canDelete = user?.permissions?.role?.delete;

  const navigateToAddUser = () => {
    navigate("/role-list/add-role");
  };

  const allColumns = [
    { key: "name", label: "Role Name" },
    { key: "created_at", label: "Created On" },
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map((c) => c.key)
  );

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get("/role");
        if (res.data.success) {
          const data = res.data.data || [];

          const mappedRows = data.map((item) => {
            let formattedDate = "-";
            if (item.created_at) {
              const utcDateString = item.created_at.endsWith("Z")
                ? item.created_at
                : `${item.created_at}Z`;
              const zonedDate = toZonedTime(utcDateString, "Asia/Kuala_Lumpur");
              formattedDate = format(zonedDate, "dd MMM yyyy, hh:mm a");
            }

            return {
              id: item.id,
              name: item.name,
              created_at: formattedDate,
            };
          });

          setRows(mappedRows);
        }
      } catch (error) {
        console.error("Error fetching roles:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/role/${id}`);
      setRows((prev) => prev.filter((row) => row.id !== id));
      setSnackbar({
        open: true,
        message: "Role deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Delete failed:", error.message);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to delete role",
        severity: "error",
      });
    }
  };

  const filteredRows = rows.filter((row) => {
    const name = row.name ? row.name.toString() : "";
    const term = searchTerm.trim().toLowerCase();
    return name.toLowerCase().includes(term);
  });

  return (
    <div>
      <div
        className="flex items-center justify-between"
        style={{ padding: "18px" }}
      >
        <div className="font-inter text-[18px] font-semibold !pl-3">Roles</div>
        <div className="flex gap-5 items-center">
          <Box className="relative w-[246px]">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search By Role"
              className="text-[14px] placeholder:text-[11px] border rounded-[5px] pl-10 pr-2 p-2 h-[40px] w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>
          {canCreate && (
            <AddButton label="Add Role" onClick={navigateToAddUser} />
          )}
        </div>
      </div>

      <div className="px-5">
        <TableLayout
          columns={allColumns}
          visibleColumns={visibleColumns}
          rows={filteredRows}
          loading={loading}
          onEdit={
            canEdit
              ? (id) => {
                  if (id !== 1) navigate(`/role-list/edit-role/${id}`);
                }
              : null
          }
          onDelete={
            canDelete
              ? (id) => {
                  if (id !== 1) handleDelete(id);
                }
              : null
          }
        />
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
